import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import { supabase } from "@/lib/supabase";
import { DEFAULT_LOGO_URL, BRAND_NAME } from "@/lib/constants";
import type { AppSettings, FiscalSettings, SocialSettings } from "@/types";

interface AppSettingsValue {
  settingsId: string | null;
  settings: AppSettings;
  social: SocialSettings;
  fiscal: FiscalSettings;
  logoUrl: string;
  loading: boolean;
  refresh: () => Promise<void>;
}

const FALLBACK: AppSettings = {
  id: "",
  company_name: BRAND_NAME,
  logo_url: null,
  favicon_url: null,
  rnc: null,
  address: null,
  phone: null,
  whatsapp: null,
  email: null,
  primary_color: null,
  theme: "light",
};

const AppSettingsContext = createContext<AppSettingsValue | undefined>(undefined);

export function AppSettingsProvider({ children }: { children: ReactNode }) {
  const [settings, setSettings] = useState<AppSettings>(FALLBACK);
  const [social, setSocial] = useState<SocialSettings>({});
  const [fiscal, setFiscal] = useState<FiscalSettings>({});
  const [settingsId, setSettingsId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const { data } = await supabase
        .from("app_settings")
        .select(
          "id, company_name, logo_url, favicon_url, rnc, address, phone, whatsapp, email, social, fiscal, primary_color, theme",
        )
        .limit(1)
        .maybeSingle();

      if (data) {
        const row = data as AppSettings & {
          social?: SocialSettings | null;
          fiscal?: FiscalSettings | null;
        };
        setSettings(row);
        setSettingsId(row.id);
        setSocial(row.social ?? {});
        setFiscal(row.fiscal ?? {});
      }
    } catch {
      // Keep fallback values so the UI always renders.
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let active = true;
    (async () => {
      if (!active) return;
      await load();
    })();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    const isDark = settings.theme === "dark";
    document.documentElement.classList.toggle("dark", isDark);
  }, [settings.theme]);

  const logoUrl = settings.logo_url || DEFAULT_LOGO_URL;

  return (
    <AppSettingsContext.Provider
      value={{ settingsId, settings, social, fiscal, logoUrl, loading, refresh: load }}
    >
      {children}
    </AppSettingsContext.Provider>
  );
}

export function useAppSettings(): AppSettingsValue {
  const ctx = useContext(AppSettingsContext);
  if (!ctx) throw new Error("useAppSettings debe usarse dentro de AppSettingsProvider");
  return ctx;
}